import * as XLSX from 'xlsx';
import {
  normalizeName,
  normalizePhone,
  isValidKoreanMobile,
  rosterMatchKey,
} from '@shared/lib/rosterNormalize';

/** 서버로 전송되는 정규화된 수강자 한 명 */
export interface NormalizedRosterRow {
  rawName: string;
  rawPhone: string;
  normalizedName: string;
  normalizedPhone: string;
}

export interface RosterDuplicate {
  name: string;
  phone: string;
  rowNumbers: number[]; // 엑셀 데이터 행 번호 (1부터, 헤더 제외)
}

export interface RosterInvalidRow {
  rowNumber: number;
  reason: string;
}

export interface RosterParseResult {
  rows: NormalizedRosterRow[]; // 유효 + 중복 없는 정상 행만
  internalDuplicates: RosterDuplicate[]; // 비어있어야 통과
  invalidRows: RosterInvalidRow[];
  totalDataRows: number;
}

// 헤더 별칭 (엑셀 파일마다 컬럼명이 다를 수 있어 유연하게 매칭)
const NAME_HEADERS = ['수강자', '이름', '성명', '성함', '회원명', '수강생'];
const PHONE_HEADERS = ['휴대폰 번호', '휴대폰번호', '전화번호', '연락처', '핸드폰', '핸드폰번호', '휴대전화', '전화'];

function findHeaderKey(keys: string[], candidates: string[]): string | null {
  const stripped = (s: string) => s.replace(/\s+/g, '').toLowerCase();
  const candStripped = candidates.map(stripped);
  for (const key of keys) {
    if (candStripped.includes(stripped(key))) return key;
  }
  return null;
}

/**
 * 과거 수업 수강 명단 엑셀 파싱 + 정규화 + 엑셀 내부 중복 검증
 * 컬럼: "수강자"(또는 별칭) / "휴대폰 번호"(또는 별칭)
 */
export async function parsePastClassRoster(file: File): Promise<RosterParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        // raw:false → 셀의 표시 문자열을 가져와 선행 0 보존 (전화번호 보호)
        const json = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
          raw: false,
          defval: '',
        });

        if (json.length === 0) {
          reject(new Error('엑셀에 데이터가 없습니다.'));
          return;
        }

        const keys = Object.keys(json[0]);
        const nameKey = findHeaderKey(keys, NAME_HEADERS);
        const phoneKey = findHeaderKey(keys, PHONE_HEADERS);

        if (!nameKey || !phoneKey) {
          reject(
            new Error(
              '엑셀 컬럼을 찾을 수 없습니다. "수강자"와 "휴대폰 번호" 컬럼이 있는지 확인해주세요.'
            )
          );
          return;
        }

        const invalidRows: RosterInvalidRow[] = [];
        const validRows: NormalizedRosterRow[] = [];

        json.forEach((row, idx) => {
          const rowNumber = idx + 1; // 데이터 기준 1부터
          const rawName = String(row[nameKey] ?? '').trim();
          const rawPhone = String(row[phoneKey] ?? '').trim();

          if (!rawName && !rawPhone) {
            // 완전 빈 행은 조용히 무시
            return;
          }
          if (!rawName) {
            invalidRows.push({ rowNumber, reason: '수강자 이름이 비어있습니다.' });
            return;
          }
          if (!rawPhone) {
            invalidRows.push({ rowNumber, reason: '휴대폰 번호가 비어있습니다.' });
            return;
          }

          const normalizedName = normalizeName(rawName);
          const normalizedPhone = normalizePhone(rawPhone);

          if (!isValidKoreanMobile(normalizedPhone)) {
            invalidRows.push({
              rowNumber,
              reason: `휴대폰 번호 형식이 올바르지 않습니다: "${rawPhone}"`,
            });
            return;
          }

          validRows.push({ rawName, rawPhone, normalizedName, normalizedPhone });
        });

        // 엑셀 내부 중복 검증 (이름+전화번호 동일 쌍)
        const seen = new Map<string, number[]>();
        validRows.forEach((r, idx) => {
          const key = rosterMatchKey(r.normalizedName, r.normalizedPhone);
          const arr = seen.get(key) || [];
          arr.push(idx + 1);
          seen.set(key, arr);
        });

        const internalDuplicates: RosterDuplicate[] = [];
        const dupKeys = new Set<string>();
        for (const [key, rowNumbers] of seen.entries()) {
          if (rowNumbers.length > 1) {
            dupKeys.add(key);
            const sample = validRows[rowNumbers[0] - 1];
            internalDuplicates.push({
              name: sample.normalizedName,
              phone: sample.normalizedPhone,
              rowNumbers,
            });
          }
        }

        // 중복 키를 가진 행은 rows에서 제외 (업로드 차단되므로 어차피 사용 안 함)
        const rows = validRows.filter(
          (r) => !dupKeys.has(rosterMatchKey(r.normalizedName, r.normalizedPhone))
        );

        resolve({
          rows,
          internalDuplicates,
          invalidRows,
          totalDataRows: validRows.length,
        });
      } catch (error) {
        reject(new Error('엑셀 파일 처리 중 오류가 발생했습니다.'));
      }
    };

    reader.onerror = () => reject(new Error('파일을 읽는 중 오류가 발생했습니다.'));
    reader.readAsArrayBuffer(file);
  });
}

/** "수강자", "휴대폰 번호" 헤더만 있는 빈 템플릿 엑셀 다운로드 */
export function downloadRosterTemplate(): void {
  const worksheet = XLSX.utils.json_to_sheet([{ 수강자: '', '휴대폰 번호': '' }], {
    header: ['수강자', '휴대폰 번호'],
  });
  worksheet['!cols'] = [{ wch: 15 }, { wch: 18 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '수강자명단');
  XLSX.writeFile(workbook, '과거수강인원_양식.xlsx');
}
